import { useForm } from 'react-hook-form';
import { StarRating } from './StarRating';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { addReviewforProduct } from '../../../../../../../../api/product-api';
import { CustomButton } from '../../../../../../../../utils/Button';
import { AllProductReadyForReview } from './AllProductReadyForReview';

const ReviewModal = ({ onClose, order }) => {
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [review, setReview] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const rating = watch('rating', 0);

  const handleRatingClick = (value) => {
    setValue('rating', value);
  };

  const { mutate, isPending } = useMutation({
    mutationKey: ['addReview', { productID: selectedProductId }],
    mutationFn: (data) => addReviewforProduct(data),

    onSuccess: (response) => {
      if (response?.success) {
        toast.success(response?.message || 'Review added successfully!', {
          className:
            'bg-green-500 text-black text-sm px-2 py-1 rounded-md min-h-0',
          bodyClassName: 'm-0 p-0',
          closeButton: false,
        });
      } else {
        toast.error(response?.message || 'Something went wrong', {
          className:
            'bg-red-500 text-black text-sm px-2 py-1 rounded-md min-h-0',
          bodyClassName: 'm-0 p-0',
          closeButton: false,
        });
      }

      onClose();
      setReview('');
    },

    onError: (error) => {
      const errorMessage = error?.message || 'Something went wrong';
      onClose();
      setReview('');
      toast.error(errorMessage, {
        className: 'bg-red-500 text-black text-sm px-2 py-1 rounded-md min-h-0',
        bodyClassName: 'm-0 p-0',
        closeButton: false,
      });
    },
  });

  const onSubmit = (data, e) => {
    e.preventDefault();
    const reviewData = { ...data, productID: selectedProductId };
    mutate(reviewData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg relative ">
        <div className="p-4 lg:px-8 lg:pt-5">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-xl font-bold text-gray-600"
          >
            &times;
          </button>

          <h2 className="font-duplet text-lg font-medium flex items-center gap-2 py-2">
            Select Product to Review
          </h2>

          <AllProductReadyForReview
            order={order}
            selectedProductId={selectedProductId}
            setSelectedProductId={setSelectedProductId}
          />
        </div>

        <div className="w-full h-[6px] bg-[#F6F6F6]"></div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-6">
          {/* Comment */}
          <div className="px-4 lg:px-8">
            <label className="font-duplet block mb-2 text-sm">
              Add Comment
            </label>
            <textarea
              {...register('review', {
                required: 'Comment is required',
                validate: (value) => {
                  const letterCount = value.replace(/[^a-zA-Z]/g, '').length;
                  return (
                    letterCount >= 10 || 'Comment must be at least 10 letters'
                  );
                },
              })}
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder="Enter comment"
              className="w-full p-3 border border-[#B0B0B0] rounded-md outline-none resize-none"
              rows="4"
            />
            {errors.review && (
              <p className="text-red-500 text-sm mt-1">
                {errors.review.message}
              </p>
            )}
          </div>

          {/* Rating */}
          <div className="px-4 lg:px-8">
            <label className="font-duplet block mb-1 text-sm">Rate item</label>
            <StarRating
              rating={rating}
              onHandleRating={handleRatingClick}
              interactive={true}
            />
            {errors.rating && (
              <p className="text-red-500 text-sm mt-1">Rating is required</p>
            )}
          </div>

          <hr className="my-3" />

          {/* Buttons */}
          <div className="font-duplet font-medium flex justify-between items-center px-4 lg:px-8">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 underline"
            >
              Cancel
            </button>

            <div>
              <CustomButton
                text={
                  isPending ? (
                    <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin inline-block" />
                  ) : (
                    'Save'
                  )
                }
                type="submit"
                disabled={
                  isPending ||
                  !selectedProductId ||
                  !watch('review') ||
                  !watch('rating')
                }
                width="10%"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;
